export const costPeriods = ['day', 'week', 'month', 'threeMonths', 'custom', 'all'] as const;
export type CostPeriod = typeof costPeriods[number];
export interface CostPeriodSelection { period: CostPeriod; start: string; end: string }
export interface CostDateRange { from?: string; to?: string }

export function readCostPeriod(params: URLSearchParams): CostPeriodSelection {
  const raw = params.get('period');
  return { period: costPeriods.includes(raw as CostPeriod) ? raw as CostPeriod : 'month',
    start: params.get('start') ?? '', end: params.get('end') ?? '' };
}
function calendarDate(value: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('Invalid date');
  const [year, month, day] = value.split('-').map(Number);
  const result = new Date(year, month - 1, day);
  if (result.getFullYear() !== year || result.getMonth() !== month - 1 || result.getDate() !== day) throw new Error('Invalid date');
  return result;
}
/** Re-evaluate on each existing query refresh, without adding a timer or unstable query key. */
export function costDateRange(selection: CostPeriodSelection, now = new Date()): CostDateRange {
  if (selection.period === 'all') return {};
  let from: Date;
  let to = now;
  if (selection.period === 'custom') {
    from = calendarDate(selection.start);
    to = calendarDate(selection.end);
    if (to < from) throw new Error('End date precedes start date');
    to.setDate(to.getDate() + 1); // Include the whole selected final day, including DST transitions.
  } else if (selection.period === 'day' || selection.period === 'week') {
    from = new Date(now.getTime() - (selection.period === 'day' ? 1 : 7) * 86400000);
  } else if (selection.period === 'month') {
    from = new Date(now.getFullYear(), now.getMonth(), 1);
  } else {
    from = new Date(now);
    from.setDate(1);
    from.setMonth(from.getMonth() - 3);
    const lastDay = new Date(from.getFullYear(), from.getMonth() + 1, 0).getDate();
    from.setDate(Math.min(now.getDate(), lastDay));
  }
  // At the exact first instant of a month, keep a valid (empty in practice) interval.
  return { from: from.toISOString(), to: new Date(Math.max(to.getTime(), from.getTime() + 1)).toISOString() };
}
export function costPeriodParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  for (const key of ['period', 'start', 'end']) if (params.has(key)) next.set(key, params.get(key)!);
  return next;
}

export function isValidCostPeriod(selection: CostPeriodSelection): boolean {
  try { costDateRange(selection); return true; } catch { return false; }
}
